import EmailConfirmationDesktop from '@/components/welcomePage/EmailConfirmationDesktop.tsx';
import EmailConfirmationMobile from '@/components/welcomePage/EmailConfirmationMobile.tsx';
import EmailFormDesktop from '@/components/welcomePage/EmailFormDesktop.tsx';
import EmailFormMobile from '@/components/welcomePage/EmailFormMobile.tsx';
import { useCheckInternalMode } from '@/hooks/useCheckInternalMode.ts';
import { useGenerateOtt } from '@/hooks/useGenerateOtt.ts';
import { useMobileMediaQuery } from '@/utils/useMobileMediaQuery.ts';
import { WelcomeCaptcha } from '@/views/welcome/components/WelcomeCaptcha.comp.tsx';
import { HttpStatusCode } from 'axios';
import React, { useState } from 'react';

const WelcomePage: React.FC = () => {
  const isMobile = useMobileMediaQuery();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const { error, generate, isGenerating } = useGenerateOtt();
  const [resendDone, setResendDone] = useState(false);
  const [isCaptchaActive, setIsCaptchaActive] = useState(false);
  const { internalMode, isLoading: isCheckingInternalMode } = useCheckInternalMode();

  const handleShowConfirmation = () => setConfirmationVisible(true);
  const handleHideConfirmation = () => {
    setConfirmationVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent, captchaToken?: string) => {
    e.preventDefault();
    await generate({
      captchaToken,
      password: internalMode ? password : undefined,
      username: email,
    });
    handleShowConfirmation();
    setIsCaptchaActive(false);
  };

  const handleResend = async () => {
    if (resendDone) {
      return;
    }
    await generate({
      password: internalMode ? password : undefined,
      username: email,
    });
    setIsCaptchaActive(false);
    setResendDone(true);
    setTimeout(() => setResendDone(false), 3000);
  };

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  if (isMobile) {
    if (error && error.response?.status === HttpStatusCode.Forbidden) {
      return (
        <WelcomeCaptcha
          error={error}
          isCaptchaActive={isCaptchaActive}
          isProcessing={isGenerating}
          onSubmit={handleSubmit}
          setIsCaptchaActive={setIsCaptchaActive}
        />
      );
    }
    return confirmationVisible ? (
      <EmailConfirmationMobile onResendClick={handleResend} resendDone={resendDone} />
    ) : (
      <EmailFormMobile
        email={email}
        hasError={!!error}
        internalMode={internalMode}
        isLoading={isGenerating || isCheckingInternalMode}
        onChangeEmail={handleChangeEmail}
        onChangePassword={handleChangePassword}
        onSubmit={handleSubmit}
        password={password}
      />
    );
  }

  return (
    <div className="position-relative">
      <EmailFormDesktop
        email={email}
        hasError={!!error}
        internalMode={internalMode}
        isLoading={isGenerating || isCheckingInternalMode}
        onChangeEmail={handleChangeEmail}
        onChangePassword={handleChangePassword}
        onSubmit={handleSubmit}
        password={password}
      />
      <EmailConfirmationDesktop
        onCloseConfirmation={handleHideConfirmation}
        onResendClick={handleResend}
        resendDone={resendDone}
        visible={confirmationVisible && !error}
      />
      <WelcomeCaptcha
        error={error}
        isCaptchaActive={isCaptchaActive}
        isProcessing={isGenerating}
        onSubmit={handleSubmit}
        setIsCaptchaActive={setIsCaptchaActive}
      />
    </div>
  );
};

export default WelcomePage;
